/* eslint-disable import/no-unresolved */
import { AppContext, idpPath, Loader, Logout as LogOut } from "@ctsu/loader";
import { GoogleAnalytics } from "@ctsu/react-ctsu-ui";
import DocumentTitle from "components/common/DocumentTitle";
import Permission from "components/common/Permission";
import { IDP } from "components/IDP";
import Loading from "components/Loader";
import Login from "components/Login";
import { createBrowserHistory } from "history";
import React, { cloneElement, memo, Suspense, useContext, useEffect } from "react";
import { Provider } from "react-redux";
import { BrowserRouter, Navigate, Route, Routes, useNavigate } from "react-router-dom";
import appRoutes from "routes/appRoutes";
import AppContent from "./AppContent";
import { useRosterPermission } from "./helpers/getAppConfig";
import { getGAId } from "./helpers/utils";
import HelpTopics from "./HelpTopics";
import store from "./stores";

function Logout() {
  return <LogOut logout={<Navigate to="/login" />} />;
}
function GotoLogin() {
  return <Navigate to="/login" />;
}

const history = createBrowserHistory();

const Refresh = memo(function Refresh() {
  const navigate = useNavigate();
  useEffect(() => {
    const timerId = setTimeout(() => navigate(-1), 20);
    return () => clearTimeout(timerId);
  }, [navigate]);
  return null;
});

function Protect() {
  const context = useContext(AppContext);
  const { session } = context;
  const authenticated = session?.username;
  if (authenticated) {
    return <Permission />;
  }
  return <GotoLogin />;
}

function ProtectedRoute({ permission, title, helpContext, element }) {
  const hasPermit = useRosterPermission(permission);
  if (hasPermit) {
    return (
      <div id="app-context" data-permission={permission}>
        <DocumentTitle title={title} />
        <HelpTopics contextKey={helpContext} />
        {cloneElement(element, {})}
      </div>
    );
  }

  return (
    <>
      <DocumentTitle title="Access Denied" />
      <Protect />
    </>
  );
}

let flatRoutes = [];
appRoutes.forEach((route) => {
  flatRoutes.push(route);
  if (route.subRoutes) {
    flatRoutes = [...flatRoutes, ...route.subRoutes];
  }
});

export default function APP() {
  return (
    <Provider store={store}>
      <Suspense
        fallback={
          <main>
            <Loading />
          </main>
        }
      >
        <Loader loadIndicator={<Loading />}>
          <BrowserRouter history={history}>
            <AppContent>
              <Routes>
                {flatRoutes.map(({ path, exact, hasPermit, permission, title, helpContext, element }) => (
                  <Route
                    key={path}
                    exact={exact}
                    path={path}
                    element={<ProtectedRoute {...{ hasPermit, permission, title, helpContext, element }} />}
                  />
                ))}
                <Route path="/refresh" element={<Refresh />} />
                <Route path={idpPath} element={<IDP />} />
                <Route exact path="/logout" element={<Logout />} />
                <Route path="/login" element={<Login />} />
                <Route path="" element={<Navigate to="/login" />} />
              </Routes>
            </AppContent>
            <GoogleAnalytics application={process.env.REACT_APP_NAME} gaTrackingId={getGAId()} />
          </BrowserRouter>
        </Loader>
      </Suspense>
    </Provider>
  );
}
